import React, { useState, useRef, useEffect, useMemo } from 'react';
import styles from './ComboInputSelect.module.css';
import { Input } from '../Input/Input';
import { Spinner } from '../Spinner/Spinner';

export type Option = {
  value: string | number;
  label: string;
};

type ComboInputSelectProps = {
  label?: string;
  inputValue: string | number;
  setInputValue: (val: string | number) => void;
  dropdownList: Option[];
  dropdownValue: Option | Option[];
  setDropdownValue: (val: Option | Option[]) => void;
  multiple?: boolean;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  loading?: boolean;
};

export const ComboInputSelect: React.FC<ComboInputSelectProps> = ({
  label,
  inputValue,
  setInputValue,
  dropdownList,
  dropdownValue,
  setDropdownValue,
  multiple = false,
  placeholder = 'Выберите...',
  error,
  disabled,
  required,
  name,
  loading,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Фильтрация списка по inputValue
  const filteredList = useMemo(() => {
    if (!inputValue) return dropdownList;
    return dropdownList.filter(opt =>
      opt.label.toLowerCase().includes(String(inputValue).toLowerCase()) ||
      String(opt.value).includes(String(inputValue))
    );
  }, [dropdownList, inputValue]);

  // Проверка выбран ли элемент (single/multiple)
  const isSelected = (opt: Option) => {
    if (multiple && Array.isArray(dropdownValue)) {
      return dropdownValue.some(v => v.value === opt.value);
    }
    if (!multiple && dropdownValue && !Array.isArray(dropdownValue)) {
      return dropdownValue.value === opt.value;
    }
    return false;
  };

  // Обработка выбора
  const handleSelect = (opt: Option) => {
    if (disabled || loading) return;
    if (multiple) {
      if (!Array.isArray(dropdownValue)) return;
      if (dropdownValue.some(v => v.value === opt.value)) {
        setDropdownValue(dropdownValue.filter(v => v.value !== opt.value));
      } else {
        setDropdownValue([...dropdownValue, opt]);
      }
    } else {
      setDropdownValue(opt);
      setOpen(false);
    }
  };

  const selectedLabel = useMemo(() => {
    if (multiple && Array.isArray(dropdownValue)) {
      return dropdownValue.map(opt => opt.label).join(', ');
    }
    if (!multiple && dropdownValue && !Array.isArray(dropdownValue)) {
      return dropdownValue.label;
    }
    return '';
  }, [dropdownValue, multiple]);

  return (
    <div className={styles.comboWrapper} ref={ref}>
      {label && (
        <label className={styles.label}>
          {label}
          {required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
        </label>
      )}
      <div className={styles.inputSelectBox}>
        <Input
          value={open ? inputValue : selectedLabel}
          onChange={e => setInputValue(e.target.value)}
          className={styles.input}
          placeholder={placeholder}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          name={name}
          error={error}
        />
        {open && !disabled && (
          <ul className={styles.dropdown}>
            {loading ? (
              <li className={styles.loading}><Spinner size="small" /> Загрузка...</li>
            ) : filteredList.length === 0 ? (
              <li className={styles.noOptions}>Нет вариантов</li>
            ) : filteredList.map(opt => (
              <li
                key={opt.value}
                className={[
                  styles.option,
                  isSelected(opt) ? styles.selected : ''
                ].join(' ')}
                onClick={() => handleSelect(opt)}
              >
                {opt.label}
                {isSelected(opt) && <span className={styles.checkMark}>✓</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
}; 